import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAutenticacion } from './AuthContext';
import {
  obtenerPacientes,
  obtenerPacientePorId,
  obtenerObrasSocialesApi,
  crearPacienteApi,
  actualizarPacienteApi,
  eliminarPacienteApi,
  cambiarEstadoPacienteApi,
  cambiarBajaPacienteApi,
  crearSolicitudPacienteApi,
  actualizarEstadoSolicitudPacienteApi,
  actualizarFechasSolicitudPacienteApi,
  eliminarSolicitudPacienteApi,
  actualizarHorariosSolicitudPacienteApi,
  actualizarCronogramaPacienteApi,
  agregarTratamientosApi,
  eliminarTratamientoApi,
  alternarTurnoApi,
  guardarAsistenciaApi,
  guardarTurnoExcepcionApi,
  moverTurnosHorarioApi,
  moverTurnosFechaApi,
  revertirTurnosFechaApi,
  obtenerUltimoCambioFechaApi,
} from '../services/api';

const ContextoPacientes = createContext(null);
const ULTIMO_CAMBIO_FECHA_STORAGE_KEY = 'cenein.ultimoCambioFecha';

function calcularEdadDesdeFecha(fechaNacimiento) {
  if (!fechaNacimiento || typeof fechaNacimiento !== 'string') return '-';
  const m = fechaNacimiento.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '-';
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || !month || !day) return '-';
  const now = new Date();
  let edad = now.getFullYear() - year;
  const mesActual = now.getMonth() + 1;
  const diaActual = now.getDate();
  if (mesActual < month || (mesActual === month && diaActual < day)) {
    edad -= 1;
  }
  return Number.isFinite(edad) && edad >= 0 ? String(edad) : '-';
}

function normalizarModulosEntrada(valor) {
  const lista = Array.isArray(valor)
    ? valor
    : String(valor || '')
        .split(',')
        .map((item) => String(item || '').trim().toUpperCase())
        .filter(Boolean);
  return Array.from(new Set(lista.filter((item) => ['MII', 'MIS', 'MIE'].includes(item))));
}

function normalizarAniosEscolares(valor) {
  const lista = Array.isArray(valor)
    ? valor
    : String(valor || '')
        .split(',')
        .map((item) => String(item || '').trim())
        .filter(Boolean);
  return Array.from(
    new Set(lista.filter((item) => ['2026', '2027', '2028', '2029', '2030'].includes(item)))
  );
}

export function ProveedorPacientes({ children }) {
  const { usuario, cargando } = useAutenticacion();
  const [pacientes, setPacientes] = useState([]);
  const [idSeleccionado, setIdSeleccionado] = useState(null);
  const [detalleSeleccionado, setDetalleSeleccionado] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const idSeleccionadoRef = useRef(null);
  const requiereDetalle = Boolean(usuario && !usuario.isAdmin &&
    !usuario.modulePermissions?.includes('admision') &&
    (usuario.modulePermissions?.includes('ficha_pacientes') || usuario.modulePermissions?.includes('ficha_pacientes_editar')));
  const requiereDetalleRef = useRef(requiereDetalle);
  requiereDetalleRef.current = requiereDetalle;
  const [obrasSociales, setObrasSociales] = useState([]);
  const [historialCambiosFecha, setHistorialCambiosFecha] = useState(() => {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    try {
      const raw = window.localStorage.getItem(ULTIMO_CAMBIO_FECHA_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return [parsed];
      return [];
    } catch (err) {
      return [];
    }
  });

  const ultimoCambioFecha = historialCambiosFecha.length > 0 ? historialCambiosFecha[historialCambiosFecha.length - 1] : null;

  const pacienteSeleccionado = useMemo(
    () => (detalleSeleccionado?.id === idSeleccionado ? detalleSeleccionado : null) ||
      pacientes.find((p) => p.id === idSeleccionado) || null,
    [pacientes, idSeleccionado, detalleSeleccionado]
  );
  const detallePacienteListo = !requiereDetalle || Boolean(
    detalleSeleccionado && detalleSeleccionado.id === idSeleccionado
  );

  const refrescarPacientes = useCallback(async (signal) => {
    const lista = await obtenerPacientes({ signal });
    setPacientes(Array.isArray(lista) ? lista : []);
    const id = idSeleccionadoRef.current;
    if (id && requiereDetalleRef.current) {
      const detalle = await obtenerPacientePorId(encodeURIComponent(id), { signal });
      if (detalle && idSeleccionadoRef.current === id) setDetalleSeleccionado(detalle);
    }
  }, []);

  useEffect(() => {
    if (!idSeleccionado || !requiereDetalle) {
      setDetalleSeleccionado(null);
      setCargandoDetalle(false);
      return undefined;
    }
    let activo = true;
    setCargandoDetalle(true);
    obtenerPacientePorId(encodeURIComponent(idSeleccionado)).then((detalle) => {
      if (!activo) return;
      setDetalleSeleccionado(detalle);
      setCargandoDetalle(false);
    });
    return () => { activo = false; };
  }, [idSeleccionado, requiereDetalle]);

  useEffect(() => {
    if (cargando) return;
    if (!usuario) {
      setPacientes([]);
      setIdSeleccionado(null);
      idSeleccionadoRef.current = null;
      setDetalleSeleccionado(null);
      return;
    }
    const controller = new AbortController();
    setPacientes([]);
    setIdSeleccionado(null);
    idSeleccionadoRef.current = null;
    setDetalleSeleccionado(null);
    refrescarPacientes(controller.signal).catch((err) => {
      if (controller.signal.aborted) return;
      console.error('No se pudieron cargar pacientes desde la API.', err);
      setPacientes([]);
    });
    return () => controller.abort();
  }, [cargando, usuario, refrescarPacientes]);

  useEffect(() => {
    if (cargando || !usuario) return undefined;
    const sincronizarSiVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      refrescarPacientes().catch((err) => {
        console.error('No se pudo sincronizar la vigencia de las autorizaciones.', err);
      });
    };
    const intervalo = window.setInterval(sincronizarSiVisible, 5 * 60 * 1000);
    window.addEventListener('focus', sincronizarSiVisible);
    return () => {
      window.clearInterval(intervalo);
      window.removeEventListener('focus', sincronizarSiVisible);
    };
  }, [cargando, usuario, refrescarPacientes]);

  useEffect(() => {
    if (cargando) return;
    if (!usuario) {
      setObrasSociales([]);
      return;
    }
    const controller = new AbortController();
    obtenerObrasSocialesApi({ signal: controller.signal })
      .then((lista) => setObrasSociales(lista))
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('No se pudieron cargar obras sociales desde la API.', err);
        setObrasSociales([]);
      });
    return () => controller.abort();
  }, [cargando, usuario]);

  useEffect(() => {
    if (cargando || !usuario) return;
    if (!usuario.isAdmin && !usuario.modulePermissions?.includes('asistencias')) {
      setHistorialCambiosFecha([]);
      return;
    }
    const controller = new AbortController();
    obtenerUltimoCambioFechaApi({ signal: controller.signal })
      .then((ultimo) => setHistorialCambiosFecha(ultimo ? [ultimo] : []))
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('No se pudo cargar el ultimo cambio de fecha.', err);
      });
    return () => controller.abort();
  }, [cargando, usuario]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      if (historialCambiosFecha && historialCambiosFecha.length > 0) {
        window.localStorage.setItem(
          ULTIMO_CAMBIO_FECHA_STORAGE_KEY,
          JSON.stringify(historialCambiosFecha)
        );
      } else {
        window.localStorage.removeItem(ULTIMO_CAMBIO_FECHA_STORAGE_KEY);
      }
    } catch (err) {
      // Ignoramos errores de almacenamiento para no bloquear la UI.
    }
  }, [historialCambiosFecha]);

  const seleccionarPaciente = (id) => {
    idSeleccionadoRef.current = id;
    setDetalleSeleccionado(null);
    setCargandoDetalle(requiereDetalleRef.current);
    setIdSeleccionado(id);
  };

  const agregarAsistencia = (pacienteId, asistencia) => {
    setPacientes((prev) =>
      prev.map((p) => {
        if (p.id !== pacienteId) return p;
        return {
          ...p,
          asistencias: [asistencia, ...p.asistencias],
          ultimaVisita: asistencia.fecha,
        };
      })
    );
    guardarAsistenciaApi(pacienteId, asistencia).catch(() => {});
  };

  const agregarPaciente = async (datos, tratamientosOverride = []) => {
    const entrada =
      typeof datos === 'string' || typeof datos === 'number'
        ? { nombre: String(datos), apellido: '', tratamientos: tratamientosOverride }
        : datos || {};
    const nombre = String(entrada.nombre || '').trim();
    const apellido = String(entrada.apellido || '').trim();
    const nombreCompleto = `${nombre} ${apellido}`.trim();
	    if (!nombreCompleto) return null;
	    const hoyArgentina = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Argentina/Buenos_Aires',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
		    const nuevoPaciente = {
		      id: `p-${Date.now()}`,
		      nombre,
		      apellido,
		      fechaNacimiento: String(
		        entrada.fechaNacimiento || entrada.birthDate || entrada.birth_date || ''
		      ).trim(),
		      edad: calcularEdadDesdeFecha(
		        String(
		          entrada.fechaNacimiento || entrada.birthDate || entrada.birth_date || ''
		        ).trim()
		      ),
		      condicion: 'Nuevo',
	      ultimaVisita: '-',
	      ultimoControlFisiatrico: entrada.ultimoControlFisiatrico || '',
        fechaAltaControlFisiatrico: entrada.fechaAltaControlFisiatrico || '',
        fechaVencimientoControlFisiatrico: entrada.fechaVencimientoControlFisiatrico || '',
        ultimoControlTrabajoSocial: entrada.ultimoControlTrabajoSocial || '',
        fechaAltaControlTrabajoSocial: entrada.fechaAltaControlTrabajoSocial || '',
        fechaVencimientoControlTrabajoSocial: entrada.fechaVencimientoControlTrabajoSocial || '',
	      dni: String(entrada.dni || '').trim(),
	      cuit: String(entrada.cuit || '').trim(),
	      nroAfiliado: String(entrada.nroAfiliado || '').trim(),
	      integracionHorario: String(entrada.integracionHorario || '').trim(),
	      diagnostico: String(entrada.diagnostico || '').trim(),
	      padreTutor: String(entrada.padreTutor || '').trim(),
	      telefonoPadreTutor: String(entrada.telefonoPadreTutor || '').trim(),
	      madreTutora: String(entrada.madreTutora || '').trim(),
	      telefonoMadreTutora: String(entrada.telefonoMadreTutora || '').trim(),
	      calle: String(entrada.calle || '').trim(),
	      numeracion: String(entrada.numeracion || '').trim(),
	      barrio: String(entrada.barrio || '').trim(),
	      piso: String(entrada.piso || '').trim(),
	      sector: String(entrada.sector || '').trim(),
	      escuela: String(entrada.escuela || '').trim(),
	      anioGrado: String(entrada.anioGrado || '').trim(),
	      turnoEscolar: String(entrada.turnoEscolar || '').trim(),
        carAnios: normalizarAniosEscolares(entrada.carAnios),
        ppiAnios: normalizarAniosEscolares(entrada.ppiAnios),
        actaAcuerdoAnios: normalizarAniosEscolares(entrada.actaAcuerdoAnios),
        autorizadoDesde: String(entrada.autorizadoDesde || '').trim(),
        autorizadoHasta: String(entrada.autorizadoHasta || '').trim(),
	      obraSocial: String(entrada.obraSocial || '').trim(),
	      modulo: normalizarModulosEntrada(entrada.modulo || entrada.modulos).join(', '),
        modulos: normalizarModulosEntrada(entrada.modulo || entrada.modulos),
		      activo: Boolean(
            entrada.autorizadoDesde &&
            entrada.autorizadoHasta &&
            String(entrada.autorizadoDesde).slice(0, 10) <= hoyArgentina &&
            hoyArgentina <= String(entrada.autorizadoHasta).slice(0, 10)
          ),
		      estado: 'no_autorizado',
		      dadoDeBaja: false,
		      fechaBaja: '',
		      estadoPaciente: 'activo',
		      parametro: Boolean(entrada.parametro),
	      tratamientos: Array.isArray(entrada.tratamientos)
	        ? entrada.tratamientos
	        : [],
      turnosPorMes: {},
      turnosOverrides: [],
      turnos: {},
      asistencias: [],
    };
    nuevoPaciente.estado = nuevoPaciente.activo ? 'autorizado' : 'no_autorizado';
    const payload = {
      ...nuevoPaciente,
      nombreCompleto,
      affiliate_number: nuevoPaciente.nroAfiliado,
      affiliateNumber: nuevoPaciente.nroAfiliado,
      numeroAfiliado: nuevoPaciente.nroAfiliado,
      turnosBase: entrada.turnosBase || {},
    };
    const guardado = await crearPacienteApi(payload);
    setPacientes((prev) => [guardado, ...prev.filter((p) => p.id !== guardado.id)]);
    return guardado;
  };

  const eliminarPaciente = (pacienteId) => {
    eliminarPacienteApi(pacienteId)
      .then(() => refrescarPacientes())
      .catch((err) => {
        console.error('No se pudo eliminar el paciente en DB.', err);
        refrescarPacientes().catch(() => {});
      });
  };

  const cambiarEstadoPaciente = async (pacienteId, activo) => {
    const activoFinal = Boolean(activo);
    try {
      const data = await cambiarEstadoPacienteApi(pacienteId, activoFinal);
      await refrescarPacientes();
      return data;
    } catch (err) {
      console.error('No se pudo actualizar el estado del paciente en DB.', err);
      await refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const cambiarBajaPaciente = (pacienteId, dadoDeBaja) => {
    const bajaFinal = Boolean(dadoDeBaja);
    const ahoraIso = new Date().toISOString();
    setPacientes((prev) =>
      prev.map((p) => {
        if (p.id !== pacienteId) return p;
        return {
          ...p,
          dadoDeBaja: bajaFinal,
          fechaBaja: bajaFinal ? (p.fechaBaja || ahoraIso) : '',
          estadoPaciente: bajaFinal ? 'baja' : 'activo',
        };
      })
    );
    cambiarBajaPacienteApi(pacienteId, bajaFinal)
      .then(() => refrescarPacientes())
      .catch((err) => {
        console.error('No se pudo actualizar la baja del paciente en DB.', err);
        refrescarPacientes().catch(() => {});
      });
  };

  const agregarTratamientos = (pacienteId, tratamientos) => {
    const nuevos = Array.isArray(tratamientos) ? tratamientos : [];
    if (!nuevos.length) return;
    setPacientes((prev) =>
      prev.map((p) => {
        if (p.id !== pacienteId) return p;
        const actuales = Array.isArray(p.tratamientos) ? p.tratamientos : [];
        const combinados = Array.from(new Set([...actuales, ...nuevos]));
        return { ...p, tratamientos: combinados };
      })
    );
    agregarTratamientosApi(pacienteId, nuevos)
      .then(() => refrescarPacientes())
      .catch((err) => {
        console.error('No se pudieron guardar los tratamientos en DB.', err);
        refrescarPacientes().catch(() => {});
      });
  };

  const eliminarTratamiento = (pacienteId, tratamiento) => {
    if (!tratamiento) return;
    setPacientes((prev) =>
      prev.map((p) => {
        if (p.id !== pacienteId) return p;
        const actuales = Array.isArray(p.tratamientos) ? p.tratamientos : [];
        const turnosPorMes = Object.fromEntries(
          Object.entries(p.turnosPorMes || {}).map(([mes, porTratamiento]) => [
            mes,
            Object.fromEntries(
              Object.entries(porTratamiento || {}).filter(
                ([key]) => key !== tratamiento
              )
            ),
          ])
        );
        return {
          ...p,
          tratamientos: actuales.filter((t) => t !== tratamiento),
          turnosPorMes,
          turnosOverrides: (p.turnosOverrides || []).filter(
            (o) => o.tratamiento !== tratamiento
          ),
        };
      })
    );
    eliminarTratamientoApi(pacienteId, tratamiento)
      .then(() => refrescarPacientes())
      .catch((err) => {
        console.error('No se pudo eliminar el tratamiento en DB.', err);
        refrescarPacientes().catch(() => {});
      });
  };

  const actualizarPaciente = (pacienteId, cambios) => {
    const actual = pacienteSeleccionado?.id === pacienteId
      ? pacienteSeleccionado : pacientes.find((p) => p.id === pacienteId);
    if (!actual) return Promise.resolve(null);
    const actualizado = { ...actual, ...cambios };
    return actualizarPacienteApi(actualizado)
      .then((guardado) => {
        if (!guardado || guardado.id !== pacienteId) {
          throw new Error('No se recibió la ficha actualizada del paciente.');
        }
        setPacientes((prev) => prev.map((p) => p.id === pacienteId ? guardado : p));
        if (idSeleccionadoRef.current === pacienteId && requiereDetalleRef.current) {
          setDetalleSeleccionado(guardado);
        }
        return guardado;
      })
      .catch((err) => {
        console.error('No se pudo actualizar el paciente en DB.', err);
        return refrescarPacientes()
          .catch(() => {})
          .then(() => {
            throw err;
          });
      });
  };

  const alternarTurno = (pacienteId, tratamiento, claveTurno, mes) => {
    if (!tratamiento || !claveTurno || !mes) return;
    setPacientes((prev) =>
      prev.map((p) => {
        if (p.id !== pacienteId) return p;
        const turnosPorMes = p.turnosPorMes || {};
        const mesActual = turnosPorMes[mes] || {};
        const lista = Array.isArray(mesActual[tratamiento])
          ? mesActual[tratamiento]
          : [];
        const existe = lista.includes(claveTurno);
        const [dia] = String(claveTurno).split('-');
        const nuevaLista = existe
          ? lista.filter((t) => t !== claveTurno)
          : [...lista.filter((t) => !String(t).startsWith(`${dia}-`)), claveTurno];
        return {
          ...p,
          turnosPorMes: {
            ...turnosPorMes,
            [mes]: {
              ...mesActual,
              [tratamiento]: nuevaLista,
            },
          },
        };
      })
    );
    alternarTurnoApi(pacienteId, tratamiento, claveTurno, mes)
      .then(() => refrescarPacientes())
      .catch((err) => {
        console.error('No se pudo guardar el turno en DB.', err);
        refrescarPacientes().catch(() => {});
      });
  };

  const guardarExcepcionTurno = (pacienteId, tratamiento, fecha, hora, activo) => {
    setPacientes((prev) =>
      prev.map((p) => {
        if (p.id !== pacienteId) return p;
        const actuales = Array.isArray(p.turnosOverrides) ? p.turnosOverrides : [];
        const filtrados = actuales.filter(
          (o) =>
            !(
              o.tratamiento === tratamiento &&
              o.fecha === fecha &&
              o.hora === hora
            )
        );
        return {
          ...p,
          turnosOverrides: [
            ...filtrados,
            { tratamiento, fecha, hora, activo },
          ],
        };
      })
    );
    guardarTurnoExcepcionApi(pacienteId, tratamiento, fecha, hora, activo)
      .then(() => refrescarPacientes())
      .catch((err) => {
        console.error('No se pudo guardar la excepcion del turno.', err);
        refrescarPacientes().catch(() => {});
      });
  };

  const moverTurnosHorario = async ({
    pacienteId,
    diaOrigen,
    diaDestino,
    mes,
  }) => {
    try {
      const resultado = await moverTurnosHorarioApi({
        fromDay: diaOrigen,
        toDay: diaDestino,
        patientId: pacienteId || undefined,
        month: mes,
      });
      await refrescarPacientes();
      return resultado;
    } catch (err) {
      console.error('No se pudo mover el horario.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const moverTurnosFecha = async ({
    pacienteId,
    fechaOrigen,
    fechaDestino,
    tratamiento,
    month,
    year,
    onlyDisable,
    obraSocialId,
  }) => {
    try {
      const resultado = await moverTurnosFechaApi({
        fromDate: fechaOrigen,
        toDate: fechaDestino,
        patientId: pacienteId || undefined,
        tratamiento: tratamiento || undefined,
        obraSocialId: obraSocialId || undefined,
        month,
        year,
        onlyDisable: Boolean(onlyDisable),
      });
      setHistorialCambiosFecha((prev) => {
        if (!resultado?.operation) return prev || [];
        return [resultado.operation];
      });
      await refrescarPacientes();
      return resultado;
    } catch (err) {
      console.error('No se pudo mover el turno por fecha.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const deshacerUltimoCambioFecha = async (overridePayload) => {
    const payload = overridePayload || ultimoCambioFecha;
    if (!payload?.operationId) {
      throw new Error('No hay un cambio de fecha para deshacer.');
    }
    try {
      const resultado = await revertirTurnosFechaApi({
        operationId: payload.operationId,
      });
      const siguiente = await obtenerUltimoCambioFechaApi().catch(() => null);
      setHistorialCambiosFecha(siguiente ? [siguiente] : []);
      await refrescarPacientes();
      return resultado;
    } catch (err) {
      console.error('No se pudo deshacer el cambio de fecha.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const guardarSolicitudPaciente = async (pacienteId, payload) => {
    try {
      const data = await crearSolicitudPacienteApi(pacienteId, payload);
      await refrescarPacientes();
      return data;
    } catch (err) {
      console.error('No se pudo guardar la solicitud en DB.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const cambiarEstadoSolicitudPaciente = async (pacienteId, solicitudId, estadoAutorizacion) => {
    try {
      const data = await actualizarEstadoSolicitudPacienteApi(
        pacienteId,
        solicitudId,
        estadoAutorizacion
      );
      await refrescarPacientes();
      return data;
    } catch (err) {
      console.error('No se pudo actualizar el estado de la autorización.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const cambiarFechasSolicitudPaciente = async (
    pacienteId,
    solicitudId,
    fechaInicio,
    fechaFin
  ) => {
    try {
      const data = await actualizarFechasSolicitudPacienteApi(
        pacienteId,
        solicitudId,
        fechaInicio,
        fechaFin
      );
      await refrescarPacientes();
      return data;
    } catch (err) {
      console.error('No se pudieron actualizar las fechas de la solicitud.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const eliminarSolicitudPaciente = async (pacienteId, solicitudId) => {
    try {
      const data = await eliminarSolicitudPacienteApi(pacienteId, solicitudId);
      await refrescarPacientes();
      return data;
    } catch (err) {
      console.error('No se pudo eliminar la solicitud.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const guardarHorariosSolicitudPaciente = async (pacienteId, solicitudId, horarios) => {
    try {
      const data = await actualizarHorariosSolicitudPacienteApi(
        pacienteId,
        solicitudId,
        horarios
      );
      await refrescarPacientes();
      return data;
    } catch (err) {
      console.error('No se pudieron actualizar los horarios de la solicitud.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const guardarCronogramaPaciente = async (pacienteId, horarios) => {
    try {
      const data = await actualizarCronogramaPacienteApi(pacienteId, horarios);
      await refrescarPacientes();
      return data;
    } catch (err) {
      console.error('No se pudo actualizar el cronograma vigente.', err);
      refrescarPacientes().catch(() => {});
      throw err;
    }
  };

  const valor = {
    pacientes,
    obrasSociales,
    pacienteSeleccionado,
    cargandoDetalle,
    detallePacienteListo,
    seleccionarPaciente,
    refrescarPacientes,
    agregarAsistencia,
    agregarPaciente,
    agregarTratamientos,
    eliminarTratamiento,
    actualizarPaciente,
    eliminarPaciente,
    cambiarEstadoPaciente,
    cambiarBajaPaciente,
    alternarTurno,
    guardarExcepcionTurno,
    moverTurnosHorario,
    moverTurnosFecha,
    ultimoCambioFecha,
    deshacerUltimoCambioFecha,
    guardarSolicitudPaciente,
    cambiarEstadoSolicitudPaciente,
    cambiarFechasSolicitudPaciente,
    eliminarSolicitudPaciente,
    guardarHorariosSolicitudPaciente,
    guardarCronogramaPaciente,
  };

  return (
    <ContextoPacientes.Provider value={valor}>
      {children}
    </ContextoPacientes.Provider>
  );
}

export function usePacientes() {
  const ctx = useContext(ContextoPacientes);
  if (!ctx) {
    throw new Error('usePacientes debe usarse dentro de ProveedorPacientes');
  }
  return ctx;
}
